'use client';
import React, { useRef, useEffect, useState } from 'react';
import { formatDate } from '@/utils/formatDate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleCalendar,
  faCircle,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';

interface TimeLineEvent {
  title: string;
  date: Date | undefined;
  isToday?: boolean;
}

interface TimeLineProps {
  timeline: TimeLineEvent[];
}

const TimeLine: React.FC<TimeLineProps> = ({ timeline }) => {
  const todayRef = useRef<HTMLLIElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const currentDate = new Date();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const updateScrollButtons = () => {
      if (currentWrapperRef) {
        const { scrollLeft, scrollWidth, clientWidth } = currentWrapperRef;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
      }
    };

    const currentWrapperRef = wrapperRef.current;
    const currentTodayRef = todayRef.current;

    updateScrollButtons();

    if (currentTodayRef && currentWrapperRef) {
      const parentRect = currentWrapperRef.getBoundingClientRect();
      const childRect = currentTodayRef.getBoundingClientRect();
      const offset = childRect.left - parentRect.left + currentWrapperRef.scrollLeft;
      const centerPosition = offset - parentRect.width / 2 + childRect.width / 2;

      currentWrapperRef.scrollTo({
        left: centerPosition,
        behavior: 'smooth',
      });
      updateScrollButtons();
    }

    window.addEventListener('resize', updateScrollButtons);
    currentWrapperRef?.addEventListener('scroll', updateScrollButtons);

    return () => {
      window.removeEventListener('resize', updateScrollButtons);
      currentWrapperRef?.removeEventListener('scroll', updateScrollButtons);
    };
  }, []);

  const scrollLeft = () => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const getMinWidthClass = (length: number) => {
    switch (length) {
      case 2:
        return 'min-w-1/2';
      case 3:
        return 'min-w-1/3';
      case 4:
        return 'min-w-1/4';
      case 5:
        return 'min-w-1/5';
      case 6:
        return 'min-w-1/6';
      case 7:
        return 'min-w-1/7';
      case 8:
        return 'min-w-1/8';
      case 9:
        return 'min-w-1/9';
      case 10:
        return 'min-w-1/10';
      default:
        return '';
    }
  };

  return (
    <div className="flex items-center">
      <button
        onClick={scrollLeft}
        className={`hidden p-2 sm:block ${canScrollLeft ? 'opacity-100' : 'cursor-not-allowed opacity-25'}`}
        disabled={!canScrollLeft}
      >
        <FontAwesomeIcon className="h-5 w-5" icon={faChevronLeft} />
      </button>
      <div ref={wrapperRef} className="flex-grow overflow-y-hidden whitespace-nowrap">
        <ul className="timeline flex py-2">
          {timeline.map((event, index) => {
            if (!event.title || !event.date) return null;
            const eventDate = new Date(event.date);
            const isPast = eventDate <= currentDate;
            const hrStyle = isPast ? 'bg-primary' : 'bg-gray';
            const refProp = event.isToday ? { ref: todayRef } : {};
            const todayClass = event.isToday ? 'font-medium' : '';
            const iconClass = !isPast ? 'opacity-50' : '';
            const icon = event.isToday ? faCircleCalendar : isPast ? faCircleCheck : faCircle;
            const minWidthClass = getMinWidthClass(timeline.length);

            return (
              <li key={index} {...refProp} className={`${minWidthClass} flex-shrink-0`}>
                {index !== 0 && <hr className={`${hrStyle}`} />}
                <>
                  <div className={`timeline-start timeline-box ${todayClass}`}>{event.title}</div>
                  <div className={`timeline-end text-sm ${todayClass}`}>{formatDate(eventDate.toDateString())}</div>
                  <div className="timeline-middle flex h-5 w-5 items-center justify-center">
                    <FontAwesomeIcon icon={icon} className={`ml-0.5 mr-0.5 h-4 w-4 ${iconClass}`} />
                  </div>
                </>
                {index !== timeline.length - 1 && (
                  <hr className={`${event.isToday ? 'bg-gray' : isPast ? 'bg-primary' : 'bg-gray'}`} />
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <button
        onClick={scrollRight}
        className={`hidden p-2 sm:block ${canScrollRight ? 'opacity-100' : 'cursor-not-allowed opacity-25'}`}
        disabled={!canScrollRight}
      >
        <FontAwesomeIcon className="h-5 w-5" icon={faChevronRight} />
      </button>
    </div>
  );
};

export default TimeLine;
